import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import TopBar from '../../Components/TopBar';
import Navbar from '../../Components/Navbar';
import Footer from '../../Components/Footer';
import './CoursesPage.css';

const formatDate = value => {
  if (!value) return 'TBA';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString();
};

const formatFee = (currency, amount) => {
  if (!amount && amount !== 0) return 'TBA';
  const safeCurrency = currency || 'RWF';
  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) return `${safeCurrency} ${amount}`;
  return `${safeCurrency} ${numericAmount.toLocaleString()}`;
};

const CoursesPage = () => {
  const [courses, setCourses] = useState([]);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    const res = await api.get('/courses');
    setCourses(res.data?.data || []);
  };

  const categories = useMemo(() => [...new Set(courses.map(c => c.category).filter(Boolean))], [courses]);

  const intakeBased = useMemo(
    () =>
      courses.flatMap(course =>
        (course.intakes || []).map(intake => ({
          course,
          intake,
        }))
      ),
    [courses]
  );

  const filtered = intakeBased.filter(({ course, intake }) => {
    const matchesCategory = category ? course.category === category : true;
    const matchesSearch = search
      ? `${course.title} ${course.description} ${course.trainer} ${intake.name}`
          .toLowerCase()
          .includes(search.toLowerCase())
      : true;
    return matchesCategory && matchesSearch;
  });

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const left = new Date(a.intake?.start_date || 0).getTime() || 0;
        const right = new Date(b.intake?.start_date || 0).getTime() || 0;
        return left - right;
      }),
    [filtered]
  );

  const clearFilters = () => {
    setSearch('');
    setCategory('');
  };

  return (
    <>
      <TopBar />
      <main className="page-about">
        <div className="container">
          <Navbar />
          <div className="py-4 lms-courses-wrap">
            <section className="lms-courses-hero mb-4">
              <div>
                <p className="lms-courses-kicker mb-2">Professional Learning</p>
                <h2 className="mb-2">Find the right course for your next career move</h2>
                <p className="lms-courses-subtitle mb-0">
                  Explore expertly designed programs with practical, market-ready skills.
                </p>
              </div>
              <div className="lms-courses-count">
                <span>{sorted.length}</span>
                <small>Open intakes</small>
              </div>
            </section>

            <section className="lms-courses-filters mb-4">
              <div className="row g-2 align-items-center">
                <div className="col-lg-6">
                  <input
                    className="form-control"
                    placeholder="Search by course, trainer, or intake"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                </div>
                <div className="col-lg-4">
                  <select
                    className="form-select"
                    value={category}
                    onChange={e => setCategory(e.target.value)}
                  >
                    <option value="">All categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-lg-2 d-grid">
                  <button type="button" className="btn btn-outline-secondary" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              </div>
            </section>

            <section className="row g-4">
              {sorted.map(({ course, intake }) => (
                <div key={`${course.id}-${intake.id}`} className="col-md-6 col-xl-4">
                  <article className="card h-100 lms-course-card">
                    <div className="card-body d-flex flex-column">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                        <span className="lms-course-chip">{course.category || 'General'}</span>
                        <span className="lms-course-intake">{intake.name}</span>
                      </div>

                      <h5 className="lms-course-title mb-2">{course.title}</h5>

                      <p className="lms-course-description mb-3">
                        {course.description || 'Course description will be updated soon.'}
                      </p>

                      <div className="lms-course-meta mb-3">
                        <div>
                          <strong>Dates:</strong> {formatDate(intake.start_date)} - {formatDate(intake.end_date)}
                        </div>
                        <div>
                          <strong>Trainer:</strong> {course.trainer || 'To be announced'}
                        </div>
                        <div>
                          <strong>Fee:</strong> {formatFee(course.fee_currency, course.fee)}
                        </div>
                      </div>

                      <div className="mt-auto d-grid gap-2">
                        <Link
                          to={`/courses/${course.id}?enroll=1&intake=${intake.id}`}
                          className="btn btn-primary"
                        >
                          Apply now
                        </Link>
                        <Link
                          to={`/courses/${course.id}?intake=${intake.id}`}
                          className="btn btn-light border"
                        >
                          View details
                        </Link>
                      </div>
                    </div>
                  </article>
                </div>
              ))}

              {!sorted.length && (
                <div className="col-12">
                  <div className="lms-courses-empty text-center">
                    <h5 className="mb-2">No matching courses found</h5>
                    <p className="text-muted mb-3">Try changing your filters to view more options.</p>
                    <button type="button" className="btn btn-outline-primary" onClick={clearFilters}>
                      Reset filters
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>
        <Footer />
      </main>
    </>
  );
};

export default CoursesPage;
